/* eslint-disable */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MdAppRegistration, MdExpandMore, MdChevronRight } from "react-icons/md";
import DashIcon from "../../icons/DashIcon";
import { usePermissions } from "../../../auth/PermissionProvider";
import { getUnassignedTicketCount } from "../../../services/ticketApi";
import { podeAcessarRota } from "../../../auth/routePermission";
import { Permissions } from "../../../auth/permissions";
import { isRouteActive } from "../activeRoute";

// Ícone por grupo da sidebar.
const GROUP_ICONS: Record<string, JSX.Element> = {
  Cadastros: <MdAppRegistration className="h-6 w-6" />,
};

export const SidebarLinks = (props: { routes: RoutesType[] }): JSX.Element => {
  const location = useLocation();
  const { routes } = props;
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});

  // Comparação por segmento, não substring: `/admin/profiles` acendia "Perfil" junto de
  // "Perfis de acesso" porque "profiles".includes("profile"). Ver `../activeRoute.ts`.
  const activeRoute = (route: RoutesType) => isRouteActive(location.pathname, route.layout, route.path);

  const { can } = usePermissions();

  /**
   * Chamados abertos que ninguem assumiu. So e' buscado por quem enxerga a fila.
   *
   * ⚠ Sondagem de 60 s, e nao tempo real: o hub existe mas so entrega comentario de UM chamado
   * aberto na tela — um contador global precisaria de outro canal. Sessenta segundos e' o
   * intervalo em que a informacao ainda e' util e a carga continua desprezivel (uma contagem no
   * banco, nao a fila inteira).
   */
  const [naFila, setNaFila] = React.useState(0);
  const podeVerChamados = can(Permissions.TicketsView);

  React.useEffect(() => {
    if (!podeVerChamados) return;

    let vivo = true;
    const buscar = async () => {
      const n = await getUnassignedTicketCount();
      if (vivo) setNaFila(n);
    };

    void buscar();
    const timer = setInterval(buscar, 60_000);

    // Limpeza obrigatoria: sem ela, cada remontagem do menu deixa um timer para tras e a sondagem
    // acelera sozinha ate virar uma requisicao por segundo.
    return () => {
      vivo = false;
      clearInterval(timer);
    };
  }, [podeVerChamados]);

  // Rotas com parâmetro (`paineis/:id`) não têm destino como link de menu.
  // Mesma regra do registro de rotas em `layout/admin` — menu e roteador não podem divergir,
  // senão um item aparece e leva ao fallback da home.
  const visible = routes.filter((r) => r.layout === "/admin" && r.permission && podeAcessarRota(can, r.permission) && !r.path.includes(":"));

  const renderLink = (route: RoutesType, key: React.Key, indent = false) => {
    const active = activeRoute(route);
    return (
      <Link key={key} to={route.layout + "/" + route.path}>
        <div
          className={`mb-0.5 flex items-center gap-3 rounded-xl px-3.5 py-2 transition-all duration-200 hover:cursor-pointer ${
            indent ? "ml-9 mr-3" : "mx-3"
          } ${
            active
              ? "bg-dv-gradient text-white shadow-dv-glow"
              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
          }`}
        >
          <span
            className={`flex h-5 w-5 items-center justify-center text-lg ${
              active ? "text-white" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {route.icon ? route.icon : <DashIcon />}
          </span>
          <p className={`text-[13px] leading-none ${active ? "font-semibold" : "font-medium"}`}>
            {route.name}
          </p>

          {/*
            Contador da fila, no MESMO desenho do badge de avisos do widget: branco cheio, anel
            magenta da marca e batida lenta. E' o mesmo significado nos dois lugares — "chegou coisa
            nova, a acao e' olhar" — e dar formas diferentes para o mesmo sinal obrigaria a pessoa a
            aprender dois vocabularios.

            `ml-auto` empurra para o canto do botao. Some sozinho quando zera: contador em zero e'
            ruido que ensina a ignorar o contador.
          */}
          {route.path === "tickets" && naFila > 0 && (
            <span
              className="nina-badge ml-auto flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-[#ff00c8] bg-white px-1.5 text-[11px] font-extrabold leading-none text-[#070710] shadow-[0_3px_10px_-1px_rgba(7,7,16,0.4)]"
              title={`${naFila} chamado(s) na fila sem responsavel`}
              aria-label={`${naFila} chamados na fila sem responsavel`}
            >
              {naFila > 99 ? "99+" : naFila}
            </span>
          )}
        </div>
      </Link>
    );
  };

  const elements: JSX.Element[] = [];
  const renderedGroups = new Set<string>();

  visible.forEach((route, index) => {
    if (route.group) {
      if (renderedGroups.has(route.group)) return;
      renderedGroups.add(route.group);

      const group = route.group;
      const children = visible.filter((r) => r.group === group);
      const hasActiveChild = children.some((c) => activeRoute(c));
      const isOpen = openGroups[group] !== undefined ? openGroups[group] : hasActiveChild;

      elements.push(
        <div key={`group-${group}`}>
          <div
            onClick={() => setOpenGroups((p) => ({ ...p, [group]: !isOpen }))}
            className={`mx-3 mb-0.5 flex cursor-pointer items-center gap-3 rounded-xl px-3.5 py-2 transition-all duration-200 ${
              hasActiveChild
                ? "text-brand-600 dark:text-dv-cyan-300"
                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
            }`}
          >
            <span className="flex h-5 w-5 items-center justify-center text-lg text-gray-500 dark:text-gray-400">
              {GROUP_ICONS[group] ?? <DashIcon />}
            </span>
            <p className="flex-1 text-[13px] font-semibold leading-none">{group}</p>
            {isOpen ? (
              <MdExpandMore className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            ) : (
              <MdChevronRight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            )}
          </div>
          {isOpen && (
            <div className="mb-1 mt-0.5">
              {children.map((c, i) => renderLink(c, `${group}-${i}`, true))}
            </div>
          )}
        </div>
      );
    } else {
      elements.push(renderLink(route, index));
    }
  });

  return <>{elements}</>;
};

export default SidebarLinks;
